/**
 * @description
 * Utility functions for domain extraction, filtering, and analysis.
 * Handles email domain parsing and data aggregation for VSCode Lens viewer.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/utils/domainUtils.test.ts
 */

import type { 
  StreamRecord, 
  DomainInfo, 
  DomainAggregation, 
  DomainFilterConfig,
  DomainStatistics,
  UserInfo,
  UserAggregation,
  UserStatistics,
  UserFilterConfig
} from '../../types/vscode-lens';

/**
 * Extract domain from email address
 * @param email - Email address to extract domain from
 * @returns Domain string or null if invalid
 * 
 * @example
 * extractDomain('user@byteable.ai') // Returns 'byteable.ai'
 * extractDomain('invalid') // Returns null
 */
export function extractDomain(email: string | null | undefined): string | null {
  if (!email || typeof email !== 'string') return null;
  
  // Match domain after @ symbol
  // Pattern: @[domain].[tld] where domain can contain letters, numbers, dots, hyphens
  const match = email.match(/@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Get email columns from data
 * Identifies columns that contain email addresses by checking for @ and . characters
 * 
 * @param data - Array of stream records
 * @returns Array of column names that contain email addresses
 * 
 * @example
 * getEmailColumns([{ user_email: 'test@example.com', name: 'John' }])
 * // Returns ['user_email']
 */
export function getEmailColumns(data: StreamRecord[]): string[] {
  if (data.length === 0) return [];
  
  const firstRecord = data[0];
  return Object.keys(firstRecord).filter(key => {
    const value = firstRecord[key];
    // Check if value looks like an email (contains @ and .)
    return typeof value === 'string' && 
           value.includes('@') && 
           value.includes('.');
  });
}

/**
 * Analyze data and extract domain information
 * Processes all records to build domain statistics and aggregations
 * 
 * @param data - Array of stream records
 * @param emailColumns - Columns to analyze for domains
 * @returns Domain aggregation with statistics
 * 
 * @example
 * const data = [
 *   { user_email: 'user1@byteable.ai' },
 *   { user_email: 'user2@byteable.ai' },
 *   { user_email: 'user3@gmail.com' }
 * ];
 * const result = analyzeDomains(data, ['user_email']);
 * // result.domains will have 2 entries: byteable.ai (2 records) and gmail.com (1 record)
 */
export function analyzeDomains(
  data: StreamRecord[],
  emailColumns: string[]
): DomainAggregation {
  const domainMap = new Map<string, DomainInfo>();
  
  // Extract domains from each record
  data.forEach((record, index) => {
    emailColumns.forEach(column => {
      const email = record[column];
      const domain = extractDomain(email as string);
      
      if (domain) {
        const existing = domainMap.get(domain);
        if (existing) {
          existing.count++;
          existing.records.push(index);
          if (!existing.columns.includes(column)) {
            existing.columns.push(column);
          }
        } else {
          domainMap.set(domain, {
            domain,
            count: 1,
            columns: [column],
            percentage: 0,
            records: [index],
          });
        }
      }
    });
  });
  
  // Calculate percentages
  const totalRecords = data.length;
  const domains = Array.from(domainMap.values()).map(d => ({
    ...d,
    percentage: totalRecords > 0 ? (d.count / totalRecords) * 100 : 0,
  }));
  
  // Sort by count descending
  domains.sort((a, b) => b.count - a.count);
  
  // Get top 10 domains
  const topDomains = domains.slice(0, 10);
  
  return {
    domains,
    totalRecords,
    emailColumns,
    topDomains,
  };
}

/**
 * Filter data by domain configuration
 * Applies domain filters based on include/exclude mode
 * 
 * @param data - Array of stream records
 * @param config - Domain filter configuration
 * @returns Filtered array of records
 * 
 * @example
 * const config = {
 *   enabled: true,
 *   mode: 'include',
 *   selectedDomains: new Set(['byteable.ai']),
 *   excludedDomains: new Set(),
 *   column: 'user_email'
 * };
 * const filtered = filterByDomain(data, config);
 * // Returns only records where user_email is from byteable.ai domain
 */
export function filterByDomain(
  data: StreamRecord[],
  config: DomainFilterConfig
): StreamRecord[] {
  if (!config.enabled || config.mode === 'all') {
    return data;
  }
  
  return data.filter(record => {
    const email = record[config.column];
    const domain = extractDomain(email as string);
    
    if (!domain) return false;
    
    if (config.mode === 'include') {
      return config.selectedDomains.has(domain);
    } else {
      return !config.excludedDomains.has(domain);
    }
  });
}

/**
 * Calculate domain statistics
 * Computes statistical summary of domain distribution
 * 
 * @param aggregation - Domain aggregation data
 * @returns Statistical summary of domains
 * 
 * @example
 * const stats = calculateDomainStatistics(aggregation);
 * console.log(stats.totalDomains); // Number of unique domains
 * console.log(stats.domainConcentration); // Percentage in largest domain
 */
export function calculateDomainStatistics(
  aggregation: DomainAggregation
): DomainStatistics {
  const { domains, totalRecords } = aggregation;
  
  if (domains.length === 0) {
    return {
      totalDomains: 0,
      averageRecordsPerDomain: 0,
      largestDomain: null,
      smallestDomain: null,
      domainConcentration: 0,
    };
  }
  
  const averageRecordsPerDomain = totalRecords / domains.length;
  const largestDomain = domains[0]; // Already sorted by count
  const smallestDomain = domains[domains.length - 1];
  const domainConcentration = totalRecords > 0 
    ? (largestDomain.count / totalRecords) * 100 
    : 0;
  
  return {
    totalDomains: domains.length,
    averageRecordsPerDomain,
    largestDomain,
    smallestDomain,
    domainConcentration,
  };
}

/**
 * Group domains by TLD (top-level domain)
 * Useful for analyzing domain distribution by TLD (.com, .ai, .org, etc.)
 * 
 * @param domains - Array of domain info
 * @returns Map of TLD to domains
 * 
 * @example
 * const grouped = groupByTLD(domains);
 * console.log(grouped.get('ai')); // All .ai domains
 * console.log(grouped.get('com')); // All .com domains
 */
export function groupByTLD(domains: DomainInfo[]): Map<string, DomainInfo[]> {
  const tldMap = new Map<string, DomainInfo[]>();
  
  domains.forEach(domain => {
    const parts = domain.domain.split('.');
    const tld = parts[parts.length - 1];
    
    const existing = tldMap.get(tld);
    if (existing) {
      existing.push(domain);
    } else {
      tldMap.set(tld, [domain]);
    }
  });
  
  return tldMap;
}

/**
 * Format domain for display
 * Truncates long domains and adds ellipsis
 * 
 * @param domain - Domain string
 * @param maxLength - Maximum length before truncation
 * @returns Formatted domain string
 */
export function formatDomain(domain: string, maxLength: number = 30): string {
  if (domain.length <= maxLength) return domain;
  return domain.substring(0, maxLength - 3) + '...';
}

/**
 * Get domain color for charts
 * Generates consistent colors for domains based on hash
 * 
 * @param domain - Domain string
 * @param index - Index in domain list (for fallback)
 * @returns Hex color string
 */
export function getDomainColor(domain: string, index: number): string {
  // Predefined color palette for consistency
  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#f97316', // orange
    '#6366f1', // indigo
  ];
  
  // Use index for consistent coloring
  return colors[index % colors.length];
}

/**
 * Analyze data and extract user information
 * Processes all records to build user statistics and aggregations
 * 
 * @param data - Array of stream records
 * @param emailColumns - Columns to analyze for users
 * @returns User aggregation with statistics
 * 
 * @example
 * const data = [
 *   { user_email: 'user1@byteable.ai' },
 *   { user_email: 'user2@byteable.ai' },
 *   { user_email: 'user3@gmail.com' }
 * ];
 * const result = analyzeUsers(data, ['user_email']);
 * // result.users will have 3 entries: one for each unique user
 */
export function analyzeUsers(
  data: StreamRecord[],
  emailColumns: string[]
): UserAggregation {
  const userMap = new Map<string, UserInfo>();
  
  // Extract users from each record
  data.forEach((record, index) => {
    emailColumns.forEach(column => {
      const email = record[column];
      
      if (email && typeof email === 'string') {
        const domain = extractDomain(email);
        
        if (domain) {
          const existing = userMap.get(email);
          if (existing) {
            existing.count++;
            existing.records.push(index);
            if (!existing.columns.includes(column)) {
              existing.columns.push(column);
            }
          } else {
            userMap.set(email, {
              user: email,
              domain,
              count: 1,
              columns: [column],
              percentage: 0,
              records: [index],
            });
          }
        }
      }
    });
  });
  
  // Calculate percentages
  const totalRecords = data.length;
  const users = Array.from(userMap.values()).map(u => ({
    ...u,
    percentage: totalRecords > 0 ? (u.count / totalRecords) * 100 : 0,
  }));
  
  // Sort by count descending
  users.sort((a, b) => b.count - a.count);
  
  // Get top 10 users
  const topUsers = users.slice(0, 10);
  
  return {
    users,
    totalRecords,
    emailColumns,
    topUsers,
  };
}

/**
 * Filter data by user configuration
 * Applies user filters based on include/exclude mode
 * 
 * @param data - Array of stream records
 * @param config - User filter configuration
 * @returns Filtered array of records
 * 
 * @example
 * const config = {
 *   enabled: true,
 *   mode: 'include',
 *   selectedUsers: new Set(['user@byteable.ai']),
 *   excludedUsers: new Set(),
 *   column: 'user_email'
 * };
 * const filtered = filterByUser(data, config);
 * // Returns only records where user_email matches selected users
 */
export function filterByUser(
  data: StreamRecord[],
  config: UserFilterConfig
): StreamRecord[] {
  if (!config.enabled || config.mode === 'all') {
    return data;
  }
  
  return data.filter(record => {
    const email = record[config.column];
    
    if (!email || typeof email !== 'string') return false;
    
    if (config.mode === 'include') {
      return config.selectedUsers.has(email);
    } else {
      return !config.excludedUsers.has(email);
    }
  });
}

/**
 * Calculate user statistics
 * Computes statistical summary of user distribution
 * 
 * @param aggregation - User aggregation data
 * @returns Statistical summary of users
 * 
 * @example
 * const stats = calculateUserStatistics(aggregation);
 * console.log(stats.totalUsers); // Number of unique users
 * console.log(stats.userConcentration); // Percentage in most active user
 */
export function calculateUserStatistics(
  aggregation: UserAggregation
): UserStatistics {
  const { users, totalRecords } = aggregation;
  
  if (users.length === 0) {
    return {
      totalUsers: 0,
      averageRecordsPerUser: 0,
      mostActiveUser: null,
      leastActiveUser: null,
      userConcentration: 0,
    };
  }
  
  const averageRecordsPerUser = totalRecords / users.length;
  const mostActiveUser = users[0]; // Already sorted by count
  const leastActiveUser = users[users.length - 1];
  const userConcentration = totalRecords > 0 
    ? (mostActiveUser.count / totalRecords) * 100 
    : 0;
  
  return {
    totalUsers: users.length,
    averageRecordsPerUser,
    mostActiveUser,
    leastActiveUser,
    userConcentration,
  };
}

/**
 * Get user color for charts
 * Generates consistent colors for users based on hash
 * 
 * @param user - User email string
 * @param index - Index in user list (for fallback)
 * @returns Hex color string
 */
export function getUserColor(user: string, index: number): string {
  // Use same color palette as domains for consistency
  return getDomainColor(user, index);
}

/**
 * Format user email for display
 * Truncates long emails and adds ellipsis
 * 
 * @param email - User email string
 * @param maxLength - Maximum length before truncation
 * @returns Formatted email string
 */
export function formatUserEmail(email: string, maxLength: number = 30): string {
  if (email.length <= maxLength) return email;
  return email.substring(0, maxLength - 3) + '...';
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */